import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import axios from '../../../../services/ApiService';
import { Assessment } from '../../../types/AssessmentTypes';
import LoadingSpinner from '../../../support/LoadingSpinner';
import { Helmet } from 'react-helmet-async';

export default function ViewAssessmentDetails() {
  const [assessmentData, setAssessmentData] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  useEffect(() => {
    if (id) {
      fetchAssessmentData(id);
    }
  }, [id]);

  const fetchAssessmentData = async (assessmentId: number) => {
    try {
      setLoading(true);
      const response = await axios.post("/getAssessment", assessmentId);
      setAssessmentData(response.data);
    } catch (error) {
      console.error('Error fetching assessment data.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>BVT Admin - View Assessment</title>
      </Helmet>
      {loading ? (
          <LoadingSpinner loading={loading} />
        ) : error ? (
          <Typography variant="h6" color="error" textAlign="center">
            {error}
          </Typography>
        ) : (
        <Box sx={{ padding: 3, position: 'relative', maxWidth: '1200px', margin: 'auto' }}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              backgroundColor: '#f5f5f5',
              borderRadius: '50%',
              padding: 1,
              boxShadow: 2,
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
            onClick={handleClose}
          >
            <CloseIcon sx={{ color: 'black' }} />
          </IconButton>

          <Card sx={{ width: '100%' }}>
            <CardHeader
              title={assessmentData.name}
              subheader={`Total Score: ${assessmentData.totalScore}`}
              sx={{
                backgroundColor: '#f5f5f5',
                color: 'black',
                boxShadow: 1,
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
              }}
            />
            <Divider />
            <CardContent sx={{marginTop: 1, paddingTop: 0}}>
              <Typography variant='caption' sx={{color: assessmentData.status === 'ACTIVE' ? 'lightgreen' : 'red'}}>
                  {assessmentData.status}
              </Typography>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {assessmentData.description}
              </Typography>

              {assessmentData.subCategories.map((subCategory) => (
                <Box key={subCategory.seqId} sx={{ marginBottom: 3, padding: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                    Subcategory: {subCategory.title}
                  </Typography>

                  {subCategory.questions.map((question) => (
                    <Box key={question.seqId} sx={{ marginBottom: 2, padding: 2, backgroundColor: '#ffffff', borderRadius: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                        Question: {question.text}
                      </Typography>

                      {question.options.map((option) => (
                        <Paper key={option.seqId} sx={{ padding: 1, marginBottom: 1, backgroundColor: '#f3f3f3' }}>
                          <Typography variant="body2">
                            Option: {option.label} (Score: {option.score})
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  ))}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
        )}
    </>
  );
}
